import { findById, etaTag, flatAll } from './nav';
import { routePath } from '@/lib/routes';

/**
 * Leaf pages: honest stubs + live utility notes. Status/ETA derive from
 * the nav tree — a leaf never carries its own date.
 * Teasers: `go` (docs page id) navigates internally; `href` is an
 * external/site link and renders with ↗.
 */

export interface LeafTeaser {
  eyebrow: string;
  t: string;
  d: string;
  cta: string;
  go?: string;
  href?: string;
}

export interface LeafData {
  title: string;
  lead: string;
  today: string[];
  heading?: string;
  foot?: string;
  teaser: LeafTeaser;
}

export const LEAVES: Record<string, LeafData> = {
  'g-routing': {
    title: 'Routing rules',
    lead: 'The full guide is being written against the shipped rule engine — the reference has the exact shapes today. What follows is true and current.',
    today: [
      'API conditions: country, device, language, time of day — the dashboard adds OS, browser, A/B splits',
      'Rules evaluate top-down; first match wins; the base url is the fallback — routing rules need Premium',
      'The dashboard’s live tester runs a simulated visitor through your rules and highlights its path',
    ],
    teaser: { eyebrow: 'OWNER PAGE', t: 'Smart routing on rii.link', cta: 'rii.link/smart-routing ↗', href: '/smart-routing', d: 'The marketing page carries the full rule tour this guide will deepen.' },
  },
  'g-domains': {
    title: 'Custom domains',
    lead: 'The connect flow is two DNS records and a short wait — we re-check DNS every minute. The API reference documents every shape; this guide will add registrar-specific walkthroughs.',
    today: [
      'CNAME + TXT records, returned by POST /v1/domains',
      'Verification is automatic — we poll DNS until it flips',
      'Renaming a slug later is safe: the old slug 301s to the new one for 30 days',
    ],
    teaser: { eyebrow: 'API SHAPES', t: 'Domains in the reference', cta: '→ API · Domains', go: 'api-domains', d: 'Connect, list, verify, disconnect — with the 409 you’ll hit if links still resolve.' },
  },
  'g-qr': {
    title: 'QR codes',
    lead: 'QR rendering is live in the dashboard and the API. This guide will grow print-workflow recipes; the shapes are already documented.',
    today: [
      'PNG for screens, SVG for print — both live via POST /v1/qr',
      'The returned url is permanent; the code points at your short link, so routing changes never need a reprint',
      'The API rejects fg/bg below 4.5:1 contrast — scanners give up past that',
    ],
    teaser: { eyebrow: 'OWNER PAGE', t: 'QR codes on rii.link', cta: 'rii.link/qr-codes ↗', href: '/qr-codes', d: 'The marketing page shows the dynamic-QR story end to end.' },
  },
  'g-analytics': {
    title: 'Analytics & UTM',
    lead: 'rii.link records campaign tags on every click from two places: the UTMs on the short link someone clicked, and the UTMs on the destination they were sent to. Tag destinations with the built-in UTM builder (or the utm field in the API), share the link, and read both breakdowns side by side.',
    today: [
      'The builder: in the dashboard, open “Campaign tags (UTM)” under a destination — in the Links page shorten form, or on a redirect in the workflow editor. Fill source, medium, campaign, term and content, check the live preview of the tagged URL, then press Apply',
      'Consistent tags: values are trimmed and source and medium are lowercased, so “Email” and “email” stay one row. A tag already on the URL is replaced, never duplicated, and every other query parameter is left as it was',
      'API and MCP: send utm on POST or PATCH /v1/links and the tags are written onto url; every link response returns them in utm (null when absent). On PATCH only the keys you send change, null removes one, and "utm": null strips them all. MCP create_link and update_link take utm too, and build_utm_url previews a tagged URL without saving',
      'Short-link tags: add ?utm_source=newsletter (or any utm_*) to the short URL you post in each channel — or set it as a QR code’s additional parameters — and each click records it. Use this for the channel',
      'Destination tags: the UTMs on the destination actually served for a click — after routing rules and splits — are recorded with it. Tag each rule destination on its own if you want its clicks to carry different tags',
      'Reading the breakdown: dashboard → Analytics, pick a domain and period, then the Campaign tags card. The Short link tab counts the tags on the URL people clicked; the Destination tab counts the tags on the page they were sent to. Each tab splits by source, medium, campaign, term and content. A single request in the request log shows both sets',
      'The two tabs answer different questions: Short link says which channel copy was clicked; Destination says which campaign the landing page was tagged with — the same values your site’s own analytics will see. A click can carry both, one, or neither',
      'Raw data: the click-log export (Settings → Export, or GET /v1/export/request-logs) has utm_* columns for the short link and dest_utm_* columns for the destination. MCP analytics_summary returns utm and destination_utm. GET /v1/links/{id}/stats groups by country, device, referrer or rule — not by UTM',
    ],
    heading: 'HOW CAMPAIGN TAGS WORK',
    teaser: { eyebrow: 'API SHAPES', t: 'The utm field in the reference', cta: '→ API · Links', go: 'api-links', d: 'Create and update with utm, the PATCH rules, and the utm object on every link.' },
  },
  'g-bulk': {
    title: 'Bulk import (CSV)',
    lead: 'There is no CSV importer today — not in the dashboard, not in the API — and this page won’t pretend otherwise. It ships with the batch pipeline.',
    today: [
      'Scripting a loop over POST /v1/links works once you have an API key — 600 req/min on Premium ≈ 36,000 links/hour',
      'Migrating from Bitly (a short stub today) sketches the export half of the job',
      'When the importer lands: CSV upload, collision review, dry-run mode',
    ],
    heading: 'WHAT WORKS TODAY',
    teaser: { eyebrow: 'RELATED', t: 'Migrating from Bitly', cta: '→ Self-serve ops', go: 'ops-migrate', d: 'A short stub today: the manual migration path, friction included.' },
  },
  'api-webhooks': {
    title: 'Webhooks',
    lead: 'There is no outbound event delivery in the API today — and this page won’t pretend otherwise. Delivery ships with the events pipeline; two real alternatives exist now.',
    today: [
      'SSE stream (GET /v1/events/stream) — live clicks on API-created links, one curl -N away',
      'Polling GET /v1/links/{id}/stats with from= is reliable for dashboards',
      'Domain verification is polled, not called back — nothing needs to be exposed on your side',
    ],
    heading: 'REAL ALTERNATIVES, TODAY',
    foot: 'WHEN DELIVERY SHIPS, THIS PAGE GETS: ENDPOINT REGISTRATION · SIGNING SECRETS · RETRY SCHEDULE',
    teaser: { eyebrow: 'LIVE TODAY', t: 'The SSE click stream', cta: '→ API · Analytics', go: 'api-analytics', d: 'The real-time surface that already exists, documented with runnable cURL.' },
  },
  'sdk-http': {
    title: 'cURL / raw HTTP',
    lead: 'The reference is the SDK: every example is runnable cURL against api.rii.link/v1 — no wrapper, nothing to install, nothing that can drift.',
    today: [
      'Auth is one header: Authorization: Bearer $RII_KEY',
      'Bodies are plain JSON; every error is one envelope',
      'Copy any cURL pane in the reference and it runs as-is — with your key and ids',
    ],
    heading: 'WHY THIS TAB IS ALWAYS REAL',
    teaser: { eyebrow: 'START HERE', t: 'Authentication', cta: '→ API · Authentication', go: 'api-auth', d: 'Bearer keys, test vs live prefixes, rotation without downtime.' },
  },
  'ops-keys': {
    title: 'API keys & rotation',
    lead: 'Create and revoke keys yourself — no ticket needed. Dashboard → API Keys, or Settings → API keys (my.rii.link/settings/api-keys); only workspace owners and admins can manage them. A key belongs to the workspace, not to whoever created it, so it keeps working if that person leaves the team.',
    today: [
      'Create with a name, an environment (Live rii_live_, or Test rii_test_ — test keys run in a separate sandbox: their links never redirect real traffic and don’t count toward the plan), and an access level: Read and write, or Read only',
      'Read only reads the links, domains and QR codes created through the API, plus per-link stats; Read and write can also create, update and delete links, add and remove domains, and create QR codes',
      'The full key is shown once — rii.link stores only a hash, so a lost key can’t be recovered; revoke it and create a new one. After that the list shows a masked prefix, environment, access, created, last used, and status',
      'At most 2 active keys per workspace. Rotate by creating the replacement, deploying it, then revoking the old one — in that order',
      'Revoking is immediate and can’t be undone: integrations on the old key start getting 401 until you give them a new one; revoked keys stay listed as Revoked',
      'Use it as Authorization: Bearer rii_live_… against https://api.rii.link/v1 — keep it server-side, never in a browser or a public repo',
    ],
    teaser: { eyebrow: 'API SHAPES', t: 'Authentication in the reference', cta: '→ API · Authentication', go: 'api-auth', d: 'The header, the prefixes, and the 401 you get when rotation goes wrong.' },
  },
  'ops-export': {
    title: 'Data export',
    lead: 'Your links and your click history are yours, and you download them yourself: the whole workspace from the dashboard, or the links you created over the API with an API key. Every export streams as one file.',
    today: [
      'Dashboard: Settings → Export, for workspace owners and admins on every plan. The Links card downloads every link, dashboard- and API-created; the Analytics card downloads the per-click request log for a date range, for all domains or one',
      'On the analytics page, Download CSV exports the clicks behind the current period and domain filter — known bots included, marked in is_bot',
      'Link formats: json is lossless and versioned (format_version) — every redirect option, response body and routing rule; csv is one row per link with the full delivery JSON-encoded in rules_json; nginx and caddy are rule files to re-host the redirects on your own server, one server block per domain',
      'nginx and Caddy can’t express every rule: geo, device, language, time-window and split rules are dropped with a comment naming each one, and that link serves its fallback destination. Keep the JSON export as the complete record',
      'Click log formats: csv or json, one row per request — timestamp, host, path, status, matched rule, bot and QR flags, country, device, browser, OS, referrer, the UTM tags on the short link (utm_*) and on the destination served (dest_utm_*). IP addresses are exported as stored: already truncated to /24 (IPv4) or /48 (IPv6)',
      'Retention: a click export starts no earlier than your plan’s analytics retention — 30 days on Free, 1 year on Premium, unlimited on Enterprise. If you ask for more, the start is moved up and the response says so in X-Export-From, X-Export-To, X-Export-Requested-From, X-Export-Clamped and X-Export-Retention-Days',
      'API: GET /v1/export/links and GET /v1/export/request-logs work with a Read only key. They cover the links created through the API in the key’s environment — the set GET /v1/links lists — and the clicks on them; test keys get an empty click export',
      'Filenames: rii-links-{workspace}-{date}.json, .csv, .nginx.conf or .Caddyfile, and rii-request-logs-{workspace}-{date}.json or .csv — {workspace} is your workspace name as a slug, {date} the UTC day of the export',
    ],
    heading: 'HOW EXPORT WORKS',
    teaser: { eyebrow: 'API SHAPES', t: 'Export in the reference', cta: '→ API · Export', go: 'api-export', d: 'Both endpoints, with runnable cURL, the formats and the retention headers.' },
  },
  'ops-migrate': {
    title: 'Migrating from Bitly',
    lead: 'The honest version: Bitly’s CSV export covers slugs and destinations, a script over POST /v1/links (with a self-serve API key) does the rest, and the friction points are listed below — not hidden.',
    today: [
      'Bitly exports slugs + destinations as CSV; a create loop ports them at up to 36,000 links/hour on Premium (600 req/min, no link cap) — Free stops at 100 active links',
      'Slug collisions come back as 409 slug_taken — never silently renamed',
      'Keep the old domain alive during cutover — 301s preserve inbound equity',
    ],
    heading: 'THE PATH, TODAY',
    teaser: { eyebrow: 'THE FULL DOSSIER', t: 'rii.link vs Bitly', cta: 'rii.link/compare/bitly ↗', href: '/compare/bitly', d: 'Feature-by-feature comparison including the migration math — and what Bitly still does best.' },
  },
  'sup-status': {
    title: 'Status & uptime',
    lead: 'rii.link/status is the source of truth: live routing latency, component health and incident history.',
    today: [
      'Component health for the routing service, API, dashboard, MCP server and email — 24 h and 30 d uptime',
      'Live routing latency: p50 and p99 from a synthetic probe, last hour',
      'Incidents and resolutions, with a 30-day history and an RSS feed',
    ],
    heading: 'THE PROMISES',
    teaser: { eyebrow: 'LIVE', t: 'rii.link/status', cta: 'Open the status page', href: routePath('status'), d: 'Live status, incident history, and routing latency.' },
  },
  'sup-changelog': {
    title: 'Changelog',
    lead: 'Every shipped change, dated — the full log lives on the site changelog, promise-tracking included. Roadmap stubs across these docs point there; when a date on a sash arrives, the entry lands on that page first.',
    today: [
      'Sept 2026 — self-serve export: links (JSON, CSV, nginx, Caddy) and the click log (CSV, JSON)',
      'Aug 2026 — MCP server: mcp.rii.link, remote-only, key-scoped',
      'Aug 2026 — SVG format on POST /v1/qr',
      'Jul 2026 — routing condition: time of day',
      'Jun 2026 — SSE click stream (/v1/events/stream)',
    ],
    heading: 'RECENT',
    teaser: { eyebrow: 'THE FULL LOG', t: 'rii.link/changelog', cta: 'rii.link/changelog ↗', href: '/changelog', d: 'Every release plus the promise ledger — what shipped, what’s still ticking, and the dates for both.' },
  },
  'sup-contact': {
    title: 'Contact',
    lead: 'Humans answer, by email — and we say plainly how fast we aim to reply.',
    today: [
      'support@rii.link — we reply within one business day',
      'Enterprise: a shared Slack channel is included',
      'Security reports: security@rii.link — see the security page for scope',
    ],
    heading: 'HOW TO REACH US',
    teaser: { eyebrow: 'OWNER PAGE', t: 'Security & trust at rii.link', cta: 'rii.link/security ↗', href: '/security', d: 'How we handle your data, and the disclosure policy.' },
  },
};

/* ─── Docs-home strips ─────────────────────────────────────────────── */

export const POPULAR: { label: string; id: string }[] = [
  { label: 'Authentication', id: 'api-auth' },
  { label: 'Create a link', id: 'api-links' },
  { label: 'MCP server', id: 'sdk-mcp' },
  { label: 'Webhooks', id: 'api-webhooks' },
  { label: 'Rate limits', id: 'api-errors' },
  { label: 'Errors', id: 'api-errors' },
];

export const GLANCE: { m: string; p: string; d: string; id: string }[] = [
  { m: 'POST', p: '/v1/links', d: 'Create a link with routing rules attached', id: 'api-links' },
  { m: 'GET', p: '/v1/links/{id}/stats', d: 'Clicks by country, device, or rule', id: 'api-analytics' },
  { m: 'GET', p: '/v1/events/stream', d: 'Live clicks on API links over SSE', id: 'api-analytics' },
  { m: 'POST', p: '/v1/qr', d: 'Print-ready QR for any API link', id: 'api-qr' },
];

/** Short chip labels for the honesty strip; ETAs derive from the nav tree
 *  so the strip is exactly the set of roadmap items, dated from data. */
const HONESTY_LABEL: Record<string, string> = {
  'api-webhooks': 'WEBHOOKS',
  'g-bulk': 'CSV IMPORT',
};

export const honestyChips = (): { label: string; id: string }[] =>
  flatAll()
    .filter((i) => i.status === 'roadmap')
    .map((i) => ({
      label: `${HONESTY_LABEL[i.id] ?? i.label.toUpperCase()} · ${etaTag(i.eta)}`,
      id: i.id,
    }));

/** WipTag props for a page id — status derives from the nav tree. */
export const leafTag = (id: string): { label: string; variant: 'green' | 'blue' | 'amber' } => {
  const item = findById(id);
  if (item?.status === 'live') return { label: 'Live', variant: 'green' };
  if (item?.status === 'roadmap') return { label: `Roadmap — ${item.eta}`, variant: 'amber' };
  return { label: 'Stub — expanding', variant: 'blue' };
};
